import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { featureFlagsApi } from '../services/api';
import { showError } from '../utils/showError';
import {
  PageHeader,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormField,
  FormSelect,
  FormCheckbox,
  FormTextarea,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner
} from '../components/common';
import { Flag, ArrowLeft, Users, Building, User } from 'lucide-react';

const CreateFeatureFlag = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mode, setMode] = useState('individual'); // individual, bulk, building
  const [buildings, setBuildings] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchUsers, setSearchUsers] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [featureTypes, setFeatureTypes] = useState([]);

  const [formData, setFormData] = useState({
    featureType: '',
    isEnabled: true,
    userId: '',
    buildingId: '',
    userIds: [],
    buildingFeatureType: '',
    buildingEnabled: true
  });

  useEffect(() => {
    fetchBuildings();
    fetchFeatureTypes();
  }, []);

  useEffect(() => {
    if (mode === 'bulk' && searchUsers) {
      const timer = setTimeout(() => {
        fetchUsers();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchUsers, mode]);

  const fetchBuildings = async () => {
    try {
      const response = await api.get('/Building', {
        params: { pageSize: 1000 }
      });
      const buildingsData = response.data.items || [];
      // Filter out buildings with invalid data
      const validBuildings = buildingsData.filter(building => 
        building && building.id && building.name && building.name.trim() !== ''
      );
      setBuildings(validBuildings);
    } catch (err) {
      console.error('Failed to fetch buildings:', err);
    }
  };

  const fetchFeatureTypes = async () => {
    try {
      // Get all available feature types from the dedicated endpoint
      const typesResponse = await featureFlagsApi.getTypes();
      const typesData = typesResponse.data || [];
      
      let formattedTypes = [];
      
      if (typesData.length > 0 && typeof typesData[0] === 'object') {
        // If the API returns objects with properties
        formattedTypes = typesData.map(typeObj => ({
          value: typeObj.value || typeObj.id || typeObj,
          name: typeObj.name || typeObj.displayName || `Feature Type ${typeObj.value || typeObj.id || typeObj}`
        }));
      } else {
        // If the API returns simple values
        const uniqueTypes = [...new Set(typesData)];
        formattedTypes = uniqueTypes.map(typeValue => ({
          value: typeValue,
          name: `Feature Type ${typeValue}`
        }));
      }
      
      // Sort by value
      formattedTypes.sort((a, b) => a.value - b.value);
      setFeatureTypes(formattedTypes);
    } catch (err) {
      console.error('Failed to fetch feature types:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await api.get('/Users', {
        params: { 
          searchString: searchUsers,
          pageSize: 50 
        }
      });
      setUsers(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);


    try {
      if (mode === 'individual') {
        const parsedFeatureType = parseInt(formData.featureType);
        if (!parsedFeatureType || isNaN(parsedFeatureType)) {
          setError('Please select a valid feature type');
          setLoading(false);
          return;
        }
        
        const payload = {
          userId: formData.userId,
          featureType: parsedFeatureType,
          isEnabled: formData.isEnabled
        };
        
        // Only include buildingId if it exists
        if (formData.buildingId && formData.buildingId !== '') {
          payload.buildingId = formData.buildingId;
        }
        await featureFlagsApi.create(payload);
        setSuccess('Feature flag created successfully!');
      } else if (mode === 'bulk') {
        if (formData.userIds.length === 0) {
          setError('Please select at least one user');
          return;
        }
        const filteredUserIds = formData.userIds.filter(id => id && typeof id === 'string' && id.trim() !== '');
        if (filteredUserIds.length === 0) {
          setError('Please select at least one valid user');
          setLoading(false);
          return;
        }
        const parsedFeatureType = parseInt(formData.featureType);
        
        if (!parsedFeatureType || isNaN(parsedFeatureType)) {
          setError('Please select a valid feature type');
          setLoading(false);
          return;
        }
        
        const payload = {
          userIds: filteredUserIds,
          featureType: parsedFeatureType,
          isEnabled: formData.isEnabled
        };
        
        // Only include buildingId if it exists
        if (formData.buildingId && formData.buildingId !== '') {
          payload.buildingId = formData.buildingId;
        }
        await featureFlagsApi.createBulk(payload);
        setSuccess(`Feature flags created for ${filteredUserIds.length} users!`);
      } else if (mode === 'building') {
        const parsedFeatureType = parseInt(formData.buildingFeatureType);
        if (!parsedFeatureType || isNaN(parsedFeatureType)) {
          setError('Please select a valid feature type');
          setLoading(false);
          return;
        }
        
        const payload = {
          buildingId: formData.buildingId,
          featureType: parsedFeatureType,
          isEnabled: formData.buildingEnabled
        };
        await featureFlagsApi.createForBuilding(payload);
        setSuccess('Feature flags created for all users in the building!');
      }

      // Reset form after success
      setTimeout(() => {
        navigate('/feature-flags');
      }, 2000);
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelection = (userId) => {
    if (!userId) return; // Prevent adding undefined/null values
    
    setFormData(prev => ({
      ...prev,
      userIds: prev.userIds.includes(userId) 
        ? prev.userIds.filter(id => id !== userId)
        : [...prev.userIds, userId]
    }));
  };

  const isFormValid = () => {
    if (mode === 'individual') {
      return formData.userId && formData.userId.trim() !== '' && formData.featureType && formData.featureType !== '';
    } else if (mode === 'bulk') {
      return formData.userIds.length > 0 && formData.featureType && formData.featureType !== '';
    } else if (mode === 'building') {
      return formData.buildingId && formData.buildingId !== '' && formData.buildingFeatureType && formData.buildingFeatureType !== '';
    }
    return false;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create Feature Flag"
        subtitle="Enable or disable features for users and buildings"
        icon={Flag}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/feature-flags')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Feature Flags
          </Button>
        }
      />

      {error && <ErrorMessage message={error} />}
      {success && <SuccessMessage message={success} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mode Selection */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Selection Mode</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setMode('individual')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                  mode === 'individual' 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <User size={20} className="mr-3 text-primary-600" />
                  <div>
                    <div className="font-medium">Individual User</div>
                    <div className="text-sm text-gray-500">Create flag for a single user</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setMode('bulk')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                  mode === 'bulk' 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Users size={20} className="mr-3 text-primary-600" />
                  <div>
                    <div className="font-medium">Bulk Users</div>
                    <div className="text-sm text-gray-500">Create flags for multiple users</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setMode('building')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                  mode === 'building' 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Building size={20} className="mr-3 text-primary-600" />
                  <div>
                    <div className="font-medium">Entire Building</div>
                    <div className="text-sm text-gray-500">Create flags for all users in a building</div>
                  </div>
                </div>
              </button>
            </div>
          </CardBody>
        </Card>

        {/* Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">
                {mode === 'individual' && 'Individual User Feature Flag'}
                {mode === 'bulk' && 'Bulk User Feature Flags'}
                {mode === 'building' && 'Building Feature Flags'}
              </h3>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleSubmit} className="space-y-6">
                {mode === 'individual' && (
                  <>
                    <FormField
                      label="User ID"
                      required
                      value={formData.userId}
                      onChange={(e) => setFormData(prev => ({ ...prev, userId: e.target.value }))}
                      placeholder="Enter user ID"
                    />

                    <FormField
                      label="Building ID (Optional)"
                      value={formData.buildingId}
                      onChange={(e) => setFormData(prev => ({ ...prev, buildingId: e.target.value }))}
                      placeholder="Enter building ID (leave empty for global)"
                    />

                    <FormSelect
                      label="Feature Type"
                      required
                      value={formData.featureType}
                      onChange={(e) => setFormData(prev => ({ ...prev, featureType: e.target.value }))}
                      options={[
                        { value: '', label: 'Select Feature Type' },
                        ...featureTypes.map(type => ({
                          value: type.value,
                          label: type.name
                        }))
                      ]}
                    />

                    <FormCheckbox
                      label="Enable Feature"
                      checked={formData.isEnabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                    />
                  </>
                )}

                {mode === 'bulk' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search and Select Users *
                      </label>
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchUsers}
                        onChange={(e) => setSearchUsers(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-3"
                      />
                      
                      {loadingUsers ? (
                        <div className="text-center py-4">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : (
                        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                          {users.length === 0 ? (
                            <div className="p-4 text-gray-500 text-center">
                              {searchUsers ? 'No users found' : 'Enter search term to find users'}
                            </div>
                          ) : (
                            users.map(user => (
                              <label
                                key={user.userId}
                                className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.userIds.includes(user.userId)}
                                  onChange={() => handleUserSelection(user.userId)}
                                  className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <div>
                                  <div className="font-medium">{user.firstName} {user.lastName}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                      
                      {formData.userIds.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          {formData.userIds.length} user(s) selected
                        </div>
                      )}
                    </div>

                    <FormField
                      label="Building ID (Optional)"
                      value={formData.buildingId}
                      onChange={(e) => setFormData(prev => ({ ...prev, buildingId: e.target.value }))}
                      placeholder="Enter building ID (leave empty for global)"
                    />

                    <FormSelect
                      label="Feature Type"
                      required
                      value={formData.featureType}
                      onChange={(e) => setFormData(prev => ({ ...prev, featureType: e.target.value }))}
                      options={[
                        { value: '', label: 'Select Feature Type' },
                        ...featureTypes.map(type => ({
                          value: type.value,
                          label: type.name
                        }))
                      ]}
                    />

                    <FormCheckbox
                      label="Enable Feature"
                      checked={formData.isEnabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                    />
                  </>
                )}

                {mode === 'building' && (
                  <>
                    <FormSelect
                      label="Building"
                      required
                      value={formData.buildingId}
                      onChange={(e) => setFormData(prev => ({ ...prev, buildingId: e.target.value }))}
                      options={[
                        { value: '', label: 'Select Building' },
                        ...buildings.map(building => ({
                          value: building.id,
                          label: building.name
                        }))
                      ]}
                    />

                    <FormSelect
                      label="Feature Type"
                      required
                      value={formData.buildingFeatureType}
                      onChange={(e) => setFormData(prev => ({ ...prev, buildingFeatureType: e.target.value }))}
                      options={[
                        { value: '', label: 'Select Feature Type' },
                        ...featureTypes.map(type => ({
                          value: type.value,
                          label: type.name
                        }))
                      ]}
                    />

                    <FormCheckbox
                      label="Enable Feature"
                      checked={formData.buildingEnabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, buildingEnabled: e.target.checked }))}
                    />
                  </>
                )}

                <div className="flex justify-end space-x-3 pt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate('/feature-flags')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading || !isFormValid()}
                  >
                    {loading ? 'Creating...' : 'Create Feature Flag'}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateFeatureFlag;